// ----------------------------------------------------------------------
// <copyright file="Izenda.QuerySourceTypeDetailsStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var extendObservable = require('mobx').extendObservable;

  var m = require('mobx');
  const {find, filter, uniqueId, some, map, reduce, pick, chain } = require('lodash/fp');

  var trackable = require('share/tracking/Izenda.Trackable');

  var fetcher = require('fetchers/Izenda.DataModelDataFetcher');
  var reportFetcher = require('fetchers/Izenda.ReportDesignerDataFetcher');
  var QuerySourceFieldDetailsStore = require('./Izenda.QuerySourceFieldDetailsStore');
  var PagingModel = require('share/components/controls/DataGrid/helpers/Izenda.PagingModel');
  var constants = require('logic/data/Izenda.DataModelConstants');

  var CalculatedFieldStore = require('logic/Izenda.CalculatedFieldStore');
  var ObjectStateConstants = require('logic/data/Izenda.ObjectStateConstants');
  var uiState = require('logic/Izenda.UiState');
  var FILTER_LOOKUP_STATUSES = require('logic/data/Izenda.FilterLookupConstants').FILTER_LOOKUP_STATUSES;

  function QuerySourceTypeDetailsVM(details) {
    details = details || {};
    details.extendedProperties = JSON.parse(details.extendedProperties || '{}');

    var colSortDir = {
      ColumnName: 'DESC'
    };

    extendObservable(trackable(this), {
      details: details,
      aliasError: '',
      executeErrorMessage: '',
      isSelected: false,
      fieldList: [],
      displayFieldList: function() {
        return this.fieldList.filter(function(field) {
          return field.details.state != ObjectStateConstants.DELETE;
        });
      },
      pageInfo: new PagingModel(colSortDir),
      editingQuerySourceField: null,
      hasDetailChange: function() {
        return some(['isDirty', true], this.fieldList);
      },
      isEditingFilter: false,
      calculatedStore: new CalculatedFieldStore()
    });
  }

  QuerySourceTypeDetailsVM.prototype.getId = function() {
    return this.details && this.details.id;
  };

  QuerySourceTypeDetailsVM.prototype.getCategory = function() {
    var details = this.details;
    return details.dataSourceCategoryId || details.dataSourceCategoryName;
  };

  QuerySourceTypeDetailsVM.prototype.setCategory = function(id, name, isNew) {
    this.details.dataSourceCategoryId = id;
    this.details.dataSourceCategoryName = name;
    this.details.isNewCategory = isNew;
    this.markDirty();
  };

  QuerySourceTypeDetailsVM.prototype.getDbName = function() {
    var details = this.details || {};

    return details.connectionName;
  };

  QuerySourceTypeDetailsVM.prototype.getDbSourceName = function() {
    return this.details.name;
  };

  QuerySourceTypeDetailsVM.prototype.getDbSourceAlias = function() {
    return this.details.alias;
  };
  QuerySourceTypeDetailsVM.prototype.getReturnedValue = function() {
    return this.details.extendedProperties.ReturnedValue;
  };
  QuerySourceTypeDetailsVM.prototype.getInputParams = function() {
    return this.details.extendedProperties.InputParams;
  };
  QuerySourceTypeDetailsVM.prototype.setDbSourceAlias = function(alias) {
    // reset value of aliasError
    this.aliasError && (this.aliasError = '');
    this.details.alias = alias;
    this.markDirty();
  };

  QuerySourceTypeDetailsVM.prototype.getFieldLevel = function() {
    var extendedProperties = this.details.extendedProperties || {};

    return extendedProperties.FieldLevel;
  };

  QuerySourceTypeDetailsVM.prototype.setFieldLevel = function(isFieldLevel) {
    var extendedProperties = (this.details.extendedProperties = this.details.extendedProperties || {});

    extendedProperties.FieldLevel = isFieldLevel;
    this.markDirty();
  };

  QuerySourceTypeDetailsVM.prototype.getExpressionLevel = function() {
    var extendedProperties = this.details.extendedProperties || {};

    return extendedProperties.ExpressionLevel;
  };

  QuerySourceTypeDetailsVM.prototype.setExpressionLevel = function(isExpressionLevel) {
    var extendedProperties = (this.details.extendedProperties = this.details.extendedProperties || {});

    extendedProperties.ExpressionLevel = isExpressionLevel;
    this.markDirty();
  };
  QuerySourceTypeDetailsVM.prototype.isDynamic = function() {
    var extendedProperties = this.details.extendedProperties || {};

    return extendedProperties.Dynamic;
  };

  QuerySourceTypeDetailsVM.prototype.setDynamic = function(isDynamic) {
    var extendedProperties = (this.details.extendedProperties = this.details.extendedProperties || {});

    // TODO - bich
    extendedProperties.Dynamic = isDynamic;
    this.markDirty();
  };

  QuerySourceTypeDetailsVM.prototype.isStatic = function() {
    var extendedProperties = this.details.extendedProperties || {};

    return extendedProperties.Static;
  };

  QuerySourceTypeDetailsVM.prototype.getQuerySourceDetailsType = function() {
    var extendedProperties = this.details.extendedProperties || {};

    // TODO - bich - stored procedure only
    return extendedProperties.storedProcType;
  };

  QuerySourceTypeDetailsVM.prototype.loadQuerySourceFields = m.action(function() {
    var data = Object.assign({}, this.details);

    data.extendedProperties = JSON.stringify(data.extendedProperties);

    this.pageInfo.buildPagingRequest();
    var sortOrders = this.pageInfo.sortOrders;

    return fetcher
      .loadQuerySourceFields(data, sortOrders, null, uiState.getTenantId())
      .then(this.populateData.bind(this));
  });

  QuerySourceTypeDetailsVM.prototype.populateData = m.action(function(querySourceTypeDetails) {
    querySourceTypeDetails = querySourceTypeDetails || {};

    var fieldList = querySourceTypeDetails.result || [];
    var querySourceFieldApprovalLevel = constants.APPROVAL_LEVEL.QUERY_SOURCE_FIELD;
    var deletedChangeLevel = constants.DETECTED_CHANGE_LEVEL.DELETED;

    var newData = map(function(fieldDetail) {
      var isDelete =
        fieldDetail.approval < querySourceFieldApprovalLevel && fieldDetail.physicalChange === deletedChangeLevel;

      if (isDelete) {
        fieldDetail.visible = false;
        fieldDetail.filterable = false;
      }

      return new QuerySourceFieldDetailsStore(fieldDetail);
    }, fieldList);

    // Allow save when have physical change
    if (some(['physicalChange', 1], fieldList)) {
      this.markDirty();
    }

    this.fieldList = newData;

    return this.fieldList;
  });

  /**
   * Delete field , only calculted field can be deleted
   * @param {Object} deletedField
   */
  QuerySourceTypeDetailsVM.prototype.deleteField = function(deletedField) {
    var deletePromise = Promise.resolve(true);

    if (String.isGuidFormat(deletedField.details.id)) {
      deletePromise = fetcher.deleteQuerySourceField(deletedField.details.id);
    }
    return deletePromise.bind(this).then(function(status) {
      if (status) {
        deletedField.details.state = ObjectStateConstants.DELETE;
        this.markDirty();
      }
      return status;
    });
  };

  /**
   * Check if the calculted field is used in existing report
   * @param {Object} deletedField
   */
  QuerySourceTypeDetailsVM.prototype.hasReportUseCalculatedField = function(deletedField) {
    if (!deletedField.details.id) return Promise.resolve(false);

    return reportFetcher.hasReportUseCalculatedField(deletedField.details.id);
  };

  (QuerySourceTypeDetailsVM.prototype.loadFieldNameForExpression = function(fieldDetails) {
    var details = fieldDetails && fieldDetails.details;

    var items = chain(this.fieldList)
      .flatMap(function(item) {
        return item.details;
      })
      .filter(function(item) {
        // Do not get deleted item
        if (
          item.state == ObjectStateConstants.DELETE ||
          item.physicalChange === constants.DETECTED_CHANGE_LEVEL.DELETED
        ) {
          return false;
        }

        // Do not get current editing item
        // - get if current editing is undefined
        if (!details || (item.id && item.state != ObjectStateConstants.UPDATE)) {
          return true;
        }

        // - filter temp item
        if (details.tempId !== undefined && item.tempId !== undefined) {
          return item.tempId !== details.tempId;
        }

        // - filter real item
        return details.id !== item.id;
      })
      .map(function(item) {
        return {
          id: item.id,
          name: item.alias || item.name,
          dataType: item.dataType || item.izendaDataType,
          description: item.expression,
          tempId: item.tempId
        };
      })
      .value();

    this.calculatedStore.expressionSelection.expressionFieldName.fetchFieldNames(items);
  }),
    /*
  * post execute store procedure
  */
    (QuerySourceTypeDetailsVM.prototype.executeStoreProc = function() {
      this.markDirty();
      var parseErrorMessage = function(messages) {
        return (
          messages &&
          reduce(
            function(result, ele) {
              var msg = reduce(
                function(childResult, childEle) {
                  return childResult ? childResult + ', ' + childEle : childEle;
                },
                '',
                ele.messages
              );
              return result ? result + ', ' + msg : msg;
            },
            '',
            messages
          )
        );
      };

      var afterExecStoredProc = function(result) {
        if (!result.success) {
          this.executeErrorMessage = parseErrorMessage(result.messages);
        } else {
          this.executeErrorMessage = '';
        }
        return result.data;
      }.bind(this);

      this.pageInfo.buildPagingRequest();
      var sortOrders = this.pageInfo.sortOrders;

      fetcher
        .executeStoredProc(this.getId(), this.getParams(), sortOrders)
        .then(afterExecStoredProc)
        .then(this.populateData.bind(this));
    });

  /*
    * Get list of params from list of schema details
    */
  QuerySourceTypeDetailsVM.prototype.getParams = function() {
    var fieldList = this.fieldList;

    return map(function(item) {
      var returnObject = pick(['id', 'name', 'filteredValue'], item.details);
      returnObject.filteredValue = JSON.stringify(returnObject.filteredValue);
      return returnObject;
    }, filter({ isParameter: true }, fieldList));
  };

  QuerySourceTypeDetailsVM.prototype.getDetails = function() {
    return this.details;
  };

  QuerySourceTypeDetailsVM.prototype.getFieldLists = function() {
    return this.fieldList;
  };

  QuerySourceTypeDetailsVM.prototype.get = function() {
    return this.fieldList;
  };

  /**
   * Add calculated field
   * TODO : Need refactor : move to model
   * @param {type} calculatedData
   */
  QuerySourceTypeDetailsVM.prototype.addCalculateField = m.action(function(calculatedData) {
    var newField = {
      alias: '',
      visible: true,
      filterable: !(calculatedData.isRunningField || calculatedData.isCompositeField),
      isCalculated: true,
      izendaDataType: calculatedData.izendaDataType,
      name: calculatedData.name,
      expression: calculatedData.expression,
      id: calculatedData.id,
      tempId: calculatedData.tempId,
      isRunningField: calculatedData.isRunningField,
      isCompositeField: calculatedData.isCompositeField,
      filterLookupStatus: FILTER_LOOKUP_STATUSES.VALID
    };

    // Add mode
    if (!newField.id && !newField.tempId) {
      newField.tempId = uniqueId();
      newField.state = ObjectStateConstants.INSERT;
      newField.position = this.fieldList.length;
      var field = new QuerySourceFieldDetailsStore(newField);
      field.markDirty();
      this.fieldList.push(field);
    } else {
      var editedField = find(function(o) {
        return (
          (newField.id && o.details.id === newField.id) || (newField.tempId && o.details.tempId === newField.tempId)
        );
      }, this.fieldList);

      if (editedField) {
        editedField.details.izendaDataType = calculatedData.izendaDataType;
        editedField.details.name = calculatedData.name;
        editedField.details.expression = calculatedData.expression;
        editedField.details.isRunningField = calculatedData.isRunningField;
        editedField.details.isCompositeField = calculatedData.isCompositeField;
        editedField.details.filterable = !(calculatedData.isRunningField || calculatedData.isCompositeField);
        // Update state = EDIT for existing calculated field
        if (editedField.details.id) {
          editedField.details.state = ObjectStateConstants.UPDATE;
        }
        editedField.markDirty();
      }
    }
    this.loadFieldNameForExpression();
    this.markDirty();
  });

  QuerySourceTypeDetailsVM.prototype.resetCalculatedStore = function() {
    this.calculatedStore = new CalculatedFieldStore();
  };

  module.exports = QuerySourceTypeDetailsVM;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.QuerySourceTypeDetailsStore.js